import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import FiltrosTemas, { OpcionFiltro } from "./FiltrosTemas";
import TemaLista from "./TemaLista";
import { useAreas, useTemasAppInfinito } from "../../queries/useTemas";
import { GuiaOpcion } from "../../types/tema";
import { useFiltrosUrl } from "../../utils/estadoLista";
import { useFinALaVista } from "../../utils/scrollInfinito";

const PAGE_SIZE = 8;

/** Lo que se espera desde la última tecla antes de consultar al servidor. */
const ESPERA_BUSQUEDA_MS = 350;

/**
 * Catálogo de temas filtrado y paginado **en el servidor**. Cada filtro cambia
 * la consulta, así que el resultado es sobre todo el catálogo y no sobre una
 * página ya cargada.
 *
 * Las categorías salen de `/areas/` —todas las áreas del sistema, no solo las
 * que hoy tienen algún tema— para que la lista no dependa de lo que se alcanzó
 * a traer.
 *
 * `mios` separa las dos secciones de la vista Temas: 0 = los del resto.
 *
 * El catálogo se recorre con scroll infinito: al llegar al final se pide otra
 * tanda. Los filtros y **cuántas tandas** se llevan cargadas viven en la URL
 * (`useFiltrosUrl`), así que al entrar a un tema y volver la lista se rearma
 * completa —no solo la primera tanda— y la fila de la que se viene sigue ahí para
 * quedar destacada. `claveUrl` prefija esos parámetros cuando hay más de un
 * listado en la página.
 */
export default function CatalogoRemoto({
  mios = 0,
  vacio = "No hay temas en el catálogo por ahora.",
  claveUrl = "",
}: {
  mios?: 0 | 1;
  vacio?: string;
  claveUrl?: string;
}) {
  const { leer, tandas, filtrar, verTandas } = useFiltrosUrl(claveUrl);
  const categoria = leer("area");
  const estado = leer("estado");
  // El nombre del guía viaja junto a su rut: el autocompletado necesita la
  // etiqueta para mostrar a quién se eligió, y por rut no hay cómo recuperarla.
  const rutGuia = leer("guia");
  const guia: GuiaOpcion | null = rutGuia ? { rut: Number(rutGuia), nombre: leer("guia_nombre") } : null;
  const favoritos = leer("favoritos") === "1";
  const q = leer("q");

  // El texto se escribe en local y recién viaja a la URL (y al servidor) cuando la
  // persona deja de teclear: una consulta por letra sería una tanda por letra.
  const [texto, setTexto] = useState(q);
  useEffect(() => {
    if (texto === q) return;
    const id = setTimeout(() => filtrar({ q: texto }), ESPERA_BUSQUEDA_MS);
    return () => clearTimeout(id);
  }, [texto, q, filtrar]);

  const { data: areas } = useAreas();
  const { data, isLoading, isError, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useTemasAppInfinito({
    mios,
    area: categoria ? Number(categoria) : "",
    estado,
    guia: guia?.rut ?? "",
    // Sin marcar no viaja: así no ensucia la consulta ni la clave de caché.
    favoritos: favoritos ? 1 : undefined,
    q,
    page_size: PAGE_SIZE,
  });

  const categorias: OpcionFiltro[] = useMemo(
    () => (areas ?? []).map((a) => ({ value: String(a.id), label: a.nombre })),
    [areas],
  );

  const cargadas = data?.pages.length ?? 0;
  const temas = useMemo(() => (data?.pages ?? []).flatMap((p) => p.results), [data]);
  const total = data?.pages[0]?.count ?? 0;
  const hayFiltro = !!(q || categoria || estado || guia || favoritos);

  // Único camino para traer una tanda, lo pida el scroll o la URL al volver de
  // una ficha: pedirla es subir la cuenta, y de ahí se encarga este efecto.
  useEffect(() => {
    if (cargadas > 0 && cargadas < tandas && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [cargadas, tandas, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const cargarMas = () => {
    if (hasNextPage && !isFetchingNextPage) verTandas(cargadas + 1);
  };
  const finDeLista = useFinALaVista<HTMLDivElement>(cargarMas, temas.length);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (isError) return <Alert severity="error">No se pudieron cargar los temas.</Alert>;

  return (
    // Se atenúa solo mientras se rehace la consulta (otro filtro), no al apilar
    // una tanda: ahí lo que llega se suma abajo y la lista de arriba no cambia.
    <Box sx={{ opacity: isFetching && !isFetchingNextPage ? 0.6 : 1, transition: "opacity .15s" }}>
      <FiltrosTemas
        busqueda={texto}
        onBusqueda={setTexto}
        categorias={categorias}
        categoria={categoria}
        onCategoria={(v) => filtrar({ area: v })}
        estado={estado}
        onEstado={(v) => filtrar({ estado: v })}
        guia={guia}
        onGuia={(v) => filtrar({ guia: v ? String(v.rut) : "", guia_nombre: v?.nombre ?? "" })}
        favoritos={favoritos}
        onFavoritos={(v) => filtrar({ favoritos: v ? "1" : "" })}
        total={total}
      />

      {total === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
          {hayFiltro ? "No hay temas con esos filtros." : vacio}
        </Typography>
      ) : (
        <>
          <TemaLista temas={temas} conFavorito />
          {/* El final de la lista, siempre pintado: es lo que el observador mira
              para traer la tanda siguiente, y el botón deja pedirla a mano. */}
          <Box
            ref={finDeLista}
            sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, minHeight: 44, mt: 1.5 }}
          >
            {isFetchingNextPage ? (
              <CircularProgress size={22} />
            ) : (
              <>
                {/* Cuánto se lleva visto; en la primera tanda no hace falta decirlo. */}
                {temas.length > PAGE_SIZE && (
                  <Typography variant="caption" color="text.secondary">
                    {hasNextPage ? `${temas.length} de ${total} temas.` : `Esos son los ${total} temas.`}
                  </Typography>
                )}
                {hasNextPage && (
                  <Button variant="text" size="small" onClick={cargarMas}>
                    Cargar más temas
                  </Button>
                )}
              </>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
